import React, { useEffect, useState } from 'react';
import { useGame, api } from '../context/GameContext';
import { Layers, Coins, Sparkles } from 'lucide-react';

const TYPE_COLORS = {
  artist: '#D4AF37', year: '#7CC4FF', rarity: '#A855F7',
  unique: '#F5D07A', ton: '#0098EA',
};

const fmt = (n) => (n || 0).toLocaleString('en-US');

// Back side of the painting card: what the collection pays this painting
// and why. Loaded lazily — only once the card is actually flipped.
export const CardBuffPanel = ({ cardId, active }) => {
  const { t, language } = useGame();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const rtl = language === 'ar';

  useEffect(() => {
    if (!active || !cardId) return;
    let cancelled = false;
    setData(null);
    setLoading(true);
    setFailed(false);
    api.get(`/cards/${cardId}/bonus`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { setData(null); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cardId, active]);

  const label = (c) => {
    const head = t(`set_${c.type}`);
    if (c.type === 'unique' || c.type === 'ton') return head;
    const value = c.type === 'rarity' ? t(`rarity_${c.key}`) : c.key;
    return `${head} · ${value}`;
  };

  return (
    <div
      data-testid="card-buff-panel"
      dir={rtl ? 'rtl' : 'ltr'}
      className="w-full h-full bg-gradient-to-br from-[#191926] to-[#0C0C14] rounded-t-2xl flex flex-col border-b border-[#D4AF37]/20 px-5 py-4 overflow-hidden"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-[#D4AF37] font-heading text-sm tracking-wide">{t('buff_title')}</p>
      </div>

      {loading && !data ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
        </div>
      ) : failed ? (
        <div className="flex-1 flex flex-col justify-center" data-testid="card-buff-error">
          <p className="text-[#E4E4E7] text-sm">{t('buff_load_failed')}</p>
        </div>
      ) : !data || !data.collection ? (
        <div className="flex-1 flex flex-col justify-center" data-testid="card-buff-no-collection">
          <p className="text-[#E4E4E7] text-sm mb-1.5">{t('buff_no_collection')}</p>
          <p className="text-[#71717A] text-[11px] leading-relaxed">{t('buff_no_collection_hint')}</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center gap-1.5 mb-2" data-testid="card-buff-collection">
            <Layers className="w-3 h-3 text-[#71717A]" />
            <span className="text-[#71717A] text-[10px] uppercase tracking-wider">{t('buff_in_collection')}</span>
            <span className="text-[#E4E4E7] text-[11px] font-medium truncate">{data.collection.name}</span>
          </div>

          {data.percent > 0 ? (
            <>
              <div className="flex items-baseline gap-2 mb-2">
                <span
                  data-testid="card-buff-total"
                  dir="ltr"
                  className="font-mono text-2xl font-bold text-[#10B981]"
                >+{data.percent}%</span>
                <span className="text-[#71717A] text-[10px]">{t('buff_total')}</span>
              </div>

              <div className="space-y-1 mb-2 overflow-y-auto" data-testid="card-buff-components">
                {data.components.map((c, i) => (
                  <div key={`${c.type}-${i}`} className="flex items-center justify-between gap-2">
                    <span className="text-[11px] truncate" style={{ color: TYPE_COLORS[c.type] || '#E4E4E7' }}>
                      {label(c)} <span dir="ltr" className="text-[#52525B]">×{c.size}</span>
                    </span>
                    <span dir="ltr" className="font-mono text-[11px] text-[#A1A1AA] shrink-0">+{c.percent}%</span>
                  </div>
                ))}
              </div>

              <div
                data-testid="card-buff-per-hour"
                className="flex items-center gap-1.5 mb-2 px-2 py-1.5 rounded-lg bg-[#10B981]/8 border border-[#10B981]/20"
              >
                <Coins className="w-3 h-3 text-[#10B981]" />
                <span dir="ltr" className="font-mono text-[11px] text-[#10B981] font-bold">
                  +{fmt(data.bonus_coins_per_hour)}
                </span>
                <span className="text-[#71717A] text-[10px] truncate">{t('buff_per_hour')}</span>
              </div>
              <p className="text-[#71717A] text-[10px] leading-snug">{t('buff_applies')}</p>
            </>
          ) : (
            <div className="flex-1 flex flex-col justify-center" data-testid="card-buff-no-sets">
              <p className="text-[#E4E4E7] text-sm mb-1.5">{t('buff_no_sets')}</p>
              <p className="text-[#71717A] text-[11px] leading-relaxed">{t('buff_no_sets_hint')}</p>
            </div>
          )}

          <p className={`text-[#52525B] text-[10px] leading-snug mt-auto pt-2`}>
            {t('buff_rules')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CardBuffPanel;
